/**
 * Network error interceptor.
 * Patches fetch and XMLHttpRequest to capture failed requests
 * into a rolling buffer.
 */

import type { NetworkEntry } from "../types";

const buffer: NetworkEntry[] = [];
let maxEntries = 10;
let installed = false;

export function installNetworkInterceptor(max: number): void {
  if (installed) return;
  installed = true;
  maxEntries = max;

  // Patch fetch
  const originalFetch = window.fetch;
  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const url =
      typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    const method = init?.method || "GET";

    try {
      const response = await originalFetch.call(window, input, init);
      if (!response.ok) {
        pushEntry(url, method, response.status);
      }
      return response;
    } catch (error) {
      pushEntry(url, method, 0, (error as Error).message);
      throw error;
    }
  };

  // Patch XMLHttpRequest
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: unknown[]) {
    (this as XMLHttpRequest & { _ipMethod: string; _ipUrl: string })._ipMethod = method;
    (this as XMLHttpRequest & { _ipUrl: string })._ipUrl =
      typeof url === "string" ? url : url.toString();
    return originalOpen.apply(this, [method, url, ...rest] as Parameters<typeof originalOpen>);
  };

  XMLHttpRequest.prototype.send = function (...args: unknown[]) {
    this.addEventListener("loadend", () => {
      if (this.status >= 400 || this.status === 0) {
        const xhrData = this as XMLHttpRequest & {
          _ipUrl: string;
          _ipMethod: string;
        };
        pushEntry(xhrData._ipUrl, xhrData._ipMethod, this.status);
      }
    });
    return originalSend.apply(this, args as Parameters<typeof originalSend>);
  };
}

function pushEntry(url: string, method: string, status: number, error?: string): void {
  // Skip our own API calls
  if (url.includes("/api/screenshot") || url.includes("/api/jira")) return;

  buffer.push({
    url: url.slice(0, 200),
    method,
    status,
    error,
    timestamp: Date.now(),
  });
  if (buffer.length > maxEntries) {
    buffer.shift();
  }
}

export function getNetworkErrors(): NetworkEntry[] {
  return [...buffer];
}
